import { signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { AnyAction, Store, StoreEnhancer } from 'redux';

/**
 * Type for a store enhanced with a reactive signal property.
 */
export type WithSignal<U extends Store<any, AnyAction>> =
    U extends Store<infer S>
        ? U & {
              /**
               * A reactive Preact signal containing the full store state.
               * The signal updates automatically when the store state changes.
               *
               * Use with computed() for derived state:
               * @example
               * const counter = computed(() => store.signal.value.counter);
               */
              readonly signal: ReadonlySignal<S>;
          }
        : never;

/**
 * Store enhancer that adds a `.signal` property containing the full store state as a reactive signal.
 *
 * Consumers are expected to use `computed()` for derived state:
 * @example
 * const store = createStore(reducer, initialState, addSignal);
 * const counter = computed(() => store.signal.value.counter);
 */
export const addSignal: StoreEnhancer<{ signal: ReadonlySignal<unknown> }> = (createStore) => {
    return (reducer, initialState) => {
        const store = createStore(reducer, initialState);

        const stateSignal: Signal<unknown> = signal(store.getState());

        store.subscribe(() => {
            stateSignal.value = store.getState();
        });

        return {
            ...store,
            signal: stateSignal as ReadonlySignal<unknown>,
        };
    };
};
